/**
 * System Catalog Constants
 *
 * Defines the hardcoded e6-system catalog that provides SQL access to query history
 * via DuckDB. This catalog appears in the schema explorer when the feature flag is enabled.
 */

// ─── Catalog identifiers ───────────────────────────────────────────

/**
 * The engine-backed system catalog, as the API names it. Distinct from
 * SYSTEM_CATALOG_NAME below, which is `e6-system` -- a catalog that
 * exists only in this UI, drawn client-side, with its queries rewritten
 * by compute-api into a CTE over the raw table. Nothing in the engine has
 * ever heard of `e6-system`.
 */
export const REAL_SYSTEM_CATALOG_NAME = 'system';

/**
 * Whether to surface the real system catalog instead of `e6-system`.
 *
 * Off while the real catalog is still being rolled out. The operator
 * creates it in every workspace, so it is present and queryable well
 * before anyone is meant to find it in the tree -- and the two disagree
 * on almost everything a reader would compare: different column names
 * (executed_by vs email, execution_duration_ms vs execution_time) and
 * different rows, since the real view returns one per query where
 * e6-system returns one per lifecycle event. Showing both would be worse
 * than showing either.
 *
 * A constant rather than a setting, deliberately. This is a rollout gate
 * with one correct value at a time, not a preference: flipping it is a
 * decision about what every user of a build sees, and it should arrive
 * through the same review a behaviour change does. It comes out entirely
 * once the rollout finishes.
 *
 * Hides it from the catalog list and the schema tree. It does NOT block
 * the catalog: `SELECT * FROM system.query.query_history` still runs for
 * anyone who types it, because the catalog is real and deployed. This
 * makes it undiscovered, not disabled.
 */
export const SHOW_REAL_SYSTEM_CATALOG = true;

/**
 * Whether this catalog is the platform's own system catalog.
 *
 * Shared because the two places that need it -- the catalog dropdown and
 * the schema tree -- hold the same objects under different TypeScript
 * types, and an earlier version of this check read a field name that
 * existed in neither (`catalog_name`). It compiled, matched nothing, and
 * shipped a build showing the real catalog and e6-system at once. Taking
 * the two values as arguments stops that depending on which type a caller
 * happens to have.
 *
 * Both come straight off the API response, which carries `catalogName`
 * and `isBuiltin` and is spread intact into the store. isBuiltin is what
 * separates the platform's catalog from a user's own catalog that happens
 * to be called `system` -- hiding theirs would be taking away something
 * they made.
 *
 * Deliberately does NOT read SHOW_REAL_SYSTEM_CATALOG. A function defined
 * here would close over the real constant, and the module mocks the tests
 * use to see the other position of the gate would silently stop working.
 * Callers combine the two.
 */
export const isPlatformSystemCatalog = (catalogName: unknown, isBuiltin: unknown): boolean =>
  catalogName === REAL_SYSTEM_CATALOG_NAME && isBuiltin === true;

export const SYSTEM_CATALOG_NAME = 'e6-system';
export const SYSTEM_CATALOG_ID = 'e6-system';
export const SYSTEM_DB_NAME = 'e6-system-db';
export const SYSTEM_TABLE_NAME = 'query_history';
export const SYSTEM_AUDIT_EVENTS_TABLE = 'audit_events';
export const SYSTEM_CATALOG_ROW_LIMIT = 50000;

// Tables assumed available when the backend /system-catalog/status response
// carries no `tables` key (DuckDB backend, older servers): query_history only.
export const DEFAULT_SYSTEM_TABLES = [SYSTEM_TABLE_NAME];

// ─── Column schema ─────────────────────────────────────────────────

export interface SystemCatalogColumn {
  fieldName: string;
  fieldType: string;
}

// Only columns that have a real, distinct source in the executor's
// query_engine_history table (verified live). Columns with no source — catalog,
// database, path, source_type, cluster_uuid, workspace_id — are omitted, as is
// workspace_name (a duplicate of workspace), so `SELECT *` returns exactly what
// carries data. Must stay in sync with flightSystemProjection in compute-api
// (qhflight_system.go).
export const SYSTEM_CATALOG_COLUMNS: SystemCatalogColumn[] = [
  { fieldName: 'added_on', fieldType: 'TIMESTAMP' },
  { fieldName: 'alias', fieldType: 'STRING' },
  { fieldName: 'client_perceived_time', fieldType: 'BIGINT' },
  { fieldName: 'cluster_name', fieldType: 'STRING' },
  { fieldName: 'day', fieldType: 'STRING' },
  { fieldName: 'email', fieldType: 'STRING' },
  { fieldName: 'end_time', fieldType: 'BIGINT' },
  { fieldName: 'error', fieldType: 'STRING' },
  { fieldName: 'execution_time', fieldType: 'BIGINT' },
  { fieldName: 'explain_analyse_output', fieldType: 'STRING' },
  { fieldName: 'hour', fieldType: 'STRING' },
  { fieldName: 'is_cached', fieldType: 'BOOLEAN' },
  { fieldName: 'month', fieldType: 'STRING' },
  { fieldName: 'parsing_time', fieldType: 'BIGINT' },
  { fieldName: 'query', fieldType: 'STRING' },
  { fieldName: 'query_hash', fieldType: 'STRING' },
  { fieldName: 'query_id', fieldType: 'STRING' },
  { fieldName: 'queueing_time', fieldType: 'BIGINT' },
  { fieldName: 'start_time', fieldType: 'BIGINT' },
  { fieldName: 'status', fieldType: 'STRING' },
  { fieldName: 'timestamp', fieldType: 'TIMESTAMP' },
  { fieldName: 'updated_on', fieldType: 'TIMESTAMP' },
  { fieldName: 'workspace', fieldType: 'STRING' },
  { fieldName: 'year', fieldType: 'BIGINT' },
];

// The audit_events system table (behind ENABLE_AUDIT_SYSTEM_TABLES on the
// backend; Flight backend only). A curated subset of the audit-sdk reader
// contract plus the promoted e6_custom_compute_nodes/e6_resource_changes
// columns and `operation` + the operator phase pair and `message`
// (extracted from the attrs JSON) — must stay in sync with flightAuditColumns in compute-api
// (qhflight_system.go).
export const AUDIT_EVENTS_COLUMNS: SystemCatalogColumn[] = [
  { fieldName: 'actor_email', fieldType: 'STRING' },
  { fieldName: 'actor_type', fieldType: 'STRING' },
  { fieldName: 'e6_custom_compute_nodes', fieldType: 'STRING' },
  { fieldName: 'e6_resource_changes', fieldType: 'STRING' },
  { fieldName: 'event_id', fieldType: 'STRING' },
  { fieldName: 'event_outcome', fieldType: 'STRING' },
  { fieldName: 'message', fieldType: 'STRING' },
  { fieldName: 'operation', fieldType: 'STRING' },
  { fieldName: 'phase_after', fieldType: 'STRING' },
  { fieldName: 'phase_before', fieldType: 'STRING' },
  { fieldName: 'resource_display_name', fieldType: 'STRING' },
  { fieldName: 'resource_type', fieldType: 'STRING' },
  { fieldName: 'severity_text', fieldType: 'STRING' },
  { fieldName: 'timestamp', fieldType: 'TIMESTAMP' },
  { fieldName: 'trace_id', fieldType: 'STRING' },
  { fieldName: 'workspace_slug', fieldType: 'STRING' },
];

// Column definitions per system table name.
export const SYSTEM_TABLE_COLUMNS: Record<string, SystemCatalogColumn[]> = {
  [SYSTEM_TABLE_NAME]: SYSTEM_CATALOG_COLUMNS,
  [SYSTEM_AUDIT_EVENTS_TABLE]: AUDIT_EVENTS_COLUMNS,
};

/**
 * Returns the hardcoded column list for a system table (empty array for
 * unknown tables — e.g. a newer backend advertising a table this UI build
 * doesn't know yet; the table still renders and is queryable, just without
 * a column listing).
 */
export function getSystemCatalogColumns(tableName: string): SystemCatalogColumn[] {
  return SYSTEM_TABLE_COLUMNS[tableName] ?? [];
}

// ─── Helpers ───────────────────────────────────────────────────────

/**
 * Returns true if the given catalog name matches the system catalog.
 */
export function isSystemCatalog(catalogName: string | null | undefined): boolean {
  return catalogName === SYSTEM_CATALOG_NAME;
}

/**
 * Checks whether the system catalog feature is enabled by reading Redux state.
 * Uses lazy import to avoid circular dependency with store module.
 * Returns false (feature disabled) on any error — never crashes the app.
 */
export function isSystemCatalogEnabled(): boolean {
  try {
    // Lazy require avoids circular dependency — store imports reducers which may import constants
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { store } = require('../store') as {
      store: { getState: () => { editor?: { catalog?: { systemCatalogEnabled?: boolean } } } };
    };
    return store.getState()?.editor?.catalog?.systemCatalogEnabled === true;
  } catch {
    return false;
  }
}

/**
 * Reads the backend-advertised system tables from Redux state (same lazy
 * store access as isSystemCatalogEnabled). Falls back to query_history only
 * when the backend didn't send a table list (DuckDB backend, older servers).
 */
export function getAvailableSystemTables(): string[] {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { store } = require('../store') as {
      store: { getState: () => { editor?: { catalog?: { systemCatalogTables?: string[] } } } };
    };
    const tables = store.getState()?.editor?.catalog?.systemCatalogTables;
    return Array.isArray(tables) && tables.length > 0 ? tables : DEFAULT_SYSTEM_TABLES;
  } catch {
    return DEFAULT_SYSTEM_TABLES;
  }
}

/**
 * Result of system catalog run validation.
 * - `proceed`: true if validation passed and execution should continue
 * - `error`: set if validation failed (empty query, missing selection)
 */
export interface SystemCatalogRunResult {
  proceed: boolean;
  error?: string;
}

// ─── Lightweight SQL preprocessor ──────────────────────────────────
//
// Goal: pull table references out of FROM/JOIN clauses without bringing
// in a full SQL parser. We avoid false positives by stripping the
// constructs that can legally contain the literal text `query_history`
// without it being a table reference: comments and string literals.

/**
 * Removes comments and single-quoted string literals from `sql` so that
 * downstream regex matching only sees the executable SQL surface.
 * - `-- ...` line comments
 * - `/* ... *\/` block comments
 * - `'...'` strings (with `''` escape sequences)
 */
function stripSqlNoise(sql: string): string {
  let s = sql;
  s = s.replace(/\/\*[\s\S]*?\*\//g, ' ');
  s = s.replace(/--[^\n\r]*/g, ' ');
  s = s.replace(/'(?:[^']|'')*'/g, "''");
  return s;
}

// Identifier shapes: bare (\w-only), double-quoted, backticked, bracketed.
// All four are legal table-name forms across SQL dialects we care about,
// and `e6-system` / `e6-system-db` need quoting because of the hyphen.
const BARE_IDENT = String.raw`[A-Za-z_][\w$]*`;
const DOUBLE_QUOTED_IDENT = String.raw`"(?:[^"]|"")*"`;
const BACKTICK_QUOTED_IDENT = '`(?:[^`]|``)*`';
const BRACKET_QUOTED_IDENT = String.raw`\[[^\]]*\]`;
const ANY_IDENT = `(?:${DOUBLE_QUOTED_IDENT}|${BACKTICK_QUOTED_IDENT}|${BRACKET_QUOTED_IDENT}|${BARE_IDENT})`;
// Up to 3-part dotted name: catalog.database.table
const DOTTED_PATH = `${ANY_IDENT}(?:\\s*\\.\\s*${ANY_IDENT}){0,2}`;
// FROM allows a comma-separated list of tables (cross join); JOIN takes a single ref.
const FROM_LIST_RE = new RegExp(
  String.raw`\bFROM\b\s+(` + DOTTED_PATH + String.raw`(?:\s*,\s*` + DOTTED_PATH + String.raw`)*)`,
  'gi'
);
const JOIN_RE = new RegExp(String.raw`\bJOIN\b\s+(` + DOTTED_PATH + `)`, 'gi');

function unquoteIdentPart(p: string): string {
  if (p.length < 2) return p;
  const first = p[0];
  const last = p[p.length - 1];
  if (first === '"' && last === '"') return p.slice(1, -1).replace(/""/g, '"');
  if (first === '`' && last === '`') return p.slice(1, -1).replace(/``/g, '`');
  if (first === '[' && last === ']') return p.slice(1, -1);
  return p;
}

export interface TableRef {
  /** The raw matched dotted path (as it appeared in the SQL). */
  raw: string;
  /** Unquoted, lowercased identifier parts: 1 (table), 2 (db.table), or 3 (catalog.db.table). */
  parts: string[];
}

/**
 * Extracts every table reference following a FROM or JOIN keyword.
 * Comment text and string-literal contents are ignored.
 *
 * Note: this is a regex extractor, not a full parser. It does not understand
 * subqueries vs base tables or every SQL feature, but it is sufficient to
 * gate DuckDB routing for the system catalog where the only legal table is
 * a literal `query_history`.
 */
function pathToRef(raw: string): TableRef {
  const parts = raw.split('.').map((p) => unquoteIdentPart(p.trim()).toLowerCase());
  return { raw, parts };
}

export function extractTableRefs(sql: string | null | undefined): TableRef[] {
  if (!sql) return [];
  const cleaned = stripSqlNoise(sql);
  const refs: TableRef[] = [];

  const fromRe = new RegExp(FROM_LIST_RE.source, FROM_LIST_RE.flags);
  let m: RegExpExecArray | null;
  while ((m = fromRe.exec(cleaned)) !== null) {
    // FROM may carry a comma-separated list (implicit cross join).
    const list = m[1] ?? '';
    for (const path of list
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)) {
      refs.push(pathToRef(path));
    }
  }

  const joinRe = new RegExp(JOIN_RE.source, JOIN_RE.flags);
  while ((m = joinRe.exec(cleaned)) !== null) {
    refs.push(pathToRef(m[1] ?? ''));
  }

  return refs;
}

/**
 * Validates and prepares a system catalog query run.
 *
 * A query is routed to the system-catalog backend only when every FROM/JOIN
 * reference resolves to one of the available system tables — either fully
 * qualified (`e6-system.e6-system-db.<table>`), schema-qualified
 * (`e6-system-db.<table>`), or as the bare table name when the user has the
 * system database selected.
 */
export function validateSystemCatalogRun(params: {
  queryTextArr: string[];
  isCatalogSelected: boolean;
  isDbSelected: boolean;
  catalogName: string | null | undefined;
  databaseName: string | null | undefined;
  systemCatalogEnabled: boolean;
  /** Table names the backend advertises; defaults to query_history only. */
  availableTables?: string[];
}): SystemCatalogRunResult {
  const {
    queryTextArr,
    isCatalogSelected,
    isDbSelected,
    catalogName,
    databaseName,
    systemCatalogEnabled,
    availableTables = DEFAULT_SYSTEM_TABLES,
  } = params;

  if (!systemCatalogEnabled || !catalogName || !isSystemCatalog(catalogName)) {
    return { proceed: false };
  }

  if (!queryTextArr?.length) {
    return { proceed: false, error: 'Looks like you forgot to write the query!' };
  }

  if (!(isCatalogSelected && isDbSelected)) {
    return { proceed: false, error: 'Please select catalog and database.' };
  }

  const tableList = availableTables.map((t) => `'${t}'`).join(', ');
  let hasBareRef = false;

  for (const q of queryTextArr) {
    const refs = extractTableRefs(q);
    if (refs.length === 0) {
      return {
        proceed: false,
        error: `System catalog query must reference a system table (${tableList}).`,
      };
    }

    for (const ref of refs) {
      const tableName = ref.parts[ref.parts.length - 1] ?? '';
      if (!availableTables.includes(tableName)) {
        return {
          proceed: false,
          error: `System catalog only supports these tables: ${tableList}.`,
        };
      }
      const fqn = `${SYSTEM_CATALOG_NAME}.${SYSTEM_DB_NAME}.${tableName}`;

      if (ref.parts.length === 3) {
        const [refCatalog, refDb] = ref.parts;
        if (refCatalog !== SYSTEM_CATALOG_NAME || refDb !== SYSTEM_DB_NAME) {
          return {
            proceed: false,
            error: `Fully qualified table must be '${fqn}'.`,
          };
        }
      } else if (ref.parts.length === 2) {
        const [refDb] = ref.parts;
        if (refDb !== SYSTEM_DB_NAME) {
          return {
            proceed: false,
            error: `Schema-qualified table must use '${SYSTEM_DB_NAME}.${tableName}'.`,
          };
        }
      } else {
        // Bare table name — relies on the selected database.
        hasBareRef = true;
      }
    }
  }

  // Bare references need the system database selected; fully/schema-qualified
  // references carry their own database, so the selection doesn't have to match.
  if (hasBareRef && databaseName !== SYSTEM_DB_NAME) {
    return {
      proceed: false,
      error: `Select the '${SYSTEM_DB_NAME}' database, or use a fully qualified name like '${SYSTEM_CATALOG_NAME}.${SYSTEM_DB_NAME}.<table>'.`,
    };
  }

  return { proceed: true };
}

/**
 * Returns the pre-built schema tree node for the system catalog.
 * This matches the SchemaDataItem structure used by SchemaTree.
 */
export function getSystemCatalogSchemaNode(tables: string[] = DEFAULT_SYSTEM_TABLES) {
  return {
    id: SYSTEM_CATALOG_ID,
    name: SYSTEM_CATALOG_NAME,
    uuid: SYSTEM_CATALOG_ID,
    metastore: 'system',
    metastore_name: 'System',
    status: 'Ready',
    unity_catalog_name: SYSTEM_CATALOG_NAME,
    children: [
      {
        id: SYSTEM_DB_NAME,
        name: SYSTEM_DB_NAME,
        children: tables.map((tableName) => ({
          id: tableName,
          name: tableName,
          children: getSystemCatalogColumns(tableName).map((col) => ({
            id: col.fieldName,
            name: col.fieldName,
            fieldName: col.fieldName,
            fieldType: col.fieldType,
          })),
        })),
      },
    ],
  };
}
